import { ContentFull, ContentHeader } from "components/layout";
import { Checkbox } from "components/ui/checkbox";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Toggle } from "components/ui/toggle";
import React, { ChangeEvent, useCallback } from "react";
import { useCurrentRegion } from "store/selectors/hooks";
import { useRegionSettings } from "store/selectors/settings";

const settingKey = "ownedReferralSources";

export const RegionSettingsReferralSources: React.FC<unknown> = () => {
    const { settings, putSetting, atLeastOneOverridden, deleteSettings, status } =
        useRegionSettings();
    const region = useCurrentRegion();
    const overridden = atLeastOneOverridden([settingKey]);

    const handleOverrideChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                await putSetting(settingKey, settings[settingKey]);
                return;
            }
            const confirmed = await fancyConfirm(
                "Use agency settings?",
                `This will remove the referral source assignment setting specific to ${region?.name} and use the agency setting instead.`,
                "Yes, use agency settings",
                "No, cancel",
                { confirmStyle: "question" },
            );
            if (confirmed) {
                await deleteSettings([settingKey]);
            }
        },
        [putSetting, deleteSettings, settings, region],
    );

    const handleEnabledChange = useCallback(
        (isChecked: boolean) => {
            putSetting(settingKey, isChecked);
        },
        [putSetting],
    );

    if (status !== "success") {
        return null;
    }

    return (
        <ContentFull>
            <ContentHeader>{`Referral sources - ${region?.name}`}</ContentHeader>
            <div className="space-y-4">
                <Checkbox
                    checked={overridden}
                    onChange={handleOverrideChange}
                    label={
                        <p>
                            Use specific referral source assignment settings for{" "}
                            <strong>{region?.name}</strong>
                        </p>
                    }
                />
                <div className="group">
                    <Toggle
                        checked={settings[settingKey]}
                        disabled={!overridden}
                        onChange={handleEnabledChange}
                        label={
                            <div>
                                <span className="text-md">
                                    Referral sources are assignable to users
                                </span>
                                <div className="text-gray-500 group-hover:text-gray-900 text-sm">
                                    Allow referral sources (both businesses and contacts)
                                    to be assigned to 1 user within the organization.
                                </div>
                            </div>
                        }
                    />
                </div>
            </div>
        </ContentFull>
    );
};
